// Iter 82.5 / Phase E2.1 — Daily Attendance Snapshots admin page.
// Materialised per-(employee, date) view of the aggregator output.
// HR can filter by employee / date range / status / department,
// drill into any cell to see the full events timeline, and force-rebuild
// a snapshot if needed (admin only).
//
// 102.4 UAT · Defect Fix 001 (2026-07) — Workflow integration: the
// snapshot detail dialog now surfaces the operator's decision-point
// actions (Administrative Action · Start/View Regularization · View
// Attendance Events). Reuses the AAS Attendance wrapper + regularization
// + events endpoints already shipped in Iter 102.4 — NO AAS contract
// change, NO new permissions, NO business behaviour change.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowsClockwise, FunnelSimple, Clock, Pause, Coffee, Warning, FileText, Gavel, ListMagnifyingGlass } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate, formatTime, formatDateTimeIST } from "../../lib/format";
import AdministrativeActionDialog from "../../components/administrative/AdministrativeActionDialog";

const STATUS_BADGE_CLS = {
    "Present": "bg-emerald-100 text-emerald-700",
    "Half Day": "bg-amber-100 text-amber-700",
    "Absent": "bg-rose-100 text-rose-700",
    "On Leave": "bg-sky-100 text-sky-700",
    "Holiday": "bg-indigo-100 text-indigo-700",
    "Weekly Off": "bg-slate-100 text-slate-600",
    "Regularized": "bg-violet-100 text-violet-700",
    "Pending Regularization": "bg-yellow-100 text-yellow-700",
};

function formatMinutes(mins) {
    if (mins == null) return "—";
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h ${m}m`;
}

// Iter 102.2 · Attendance Timezone Cleanup — the previous local `formatTime`
// helper has been removed. Timestamps now come from `lib/format` (IST-forced).

export default function AttendanceSnapshots() {
    const { can } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [meta, setMeta] = useState({ attendance_statuses: [] });
    const [hrMeta, setHrMeta] = useState({ departments: [] });
    const [employees, setEmployees] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        employee_id: searchParams.get("employee_id") || "",
        from_date: searchParams.get("from_date") || "",
        to_date: searchParams.get("to_date") || "",
        attendance_status: "", department: "",
    });
    const [page, setPage] = useState(1);

    // Detail dialog
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [rebuilding, setRebuilding] = useState(false);

    // 102.4 UAT · Defect Fix 001 — Administrative Action dialog state.
    // Policies are cached per session so the AA dialog opens instantly
    // when HR clicks the button from the snapshot detail footer.
    const [aaOpen, setAaOpen] = useState(false);
    const [aaPrefill, setAaPrefill] = useState({ employeeId: "", date: "" });
    const [policies, setPolicies] = useState({});
    const [actionTypes, setActionTypes] = useState([]);

    const reloadRefs = useCallback(async () => {
        try {
            const [m, hm, e] = await Promise.all([
                api.get("/attendance/events/meta"),
                api.get("/hr/employee/meta"),
                api.get("/hr/employee/list", { params: { limit: 500 } }),
            ]);
            setMeta(m.data);
            setHrMeta(hm.data || {});
            setEmployees(e.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load reference data"));
        }
    }, []);

    // 102.4 UAT · Defect Fix 001 — Attendance policy cache for the AA
    // dialog. Non-fatal: HR without `attendance:admin:action` gets a 403
    // here and we simply keep the button hidden by the `can()` gate.
    const reloadPolicies = useCallback(async () => {
        if (!can("attendance:admin:action")) return;
        try {
            const { data } = await api.get("/administrative-action/policies", {
                params: { module: "attendance" },
            });
            const dict = {};
            (data.rows || []).forEach((r) => { dict[r.action_type] = r; });
            setPolicies(dict);
            const preferred = ["correction", "regularization", "reopening",
                               "restoration", "cancellation", "approval"];
            const ordered = preferred.filter((t) => dict[t]);
            const remaining = Object.keys(dict).filter((t) => !preferred.includes(t));
            setActionTypes([...ordered, ...remaining]);
        } catch (err) {
            // Silent — the AA button gate hides the dialog anyway.
            console.warn("AA policies load skipped:", err?.message);
        }
    }, [can]);

    const reloadRows = useCallback(async () => {
        try {
            const params = { page, page_size: 50 };
            for (const [k, v] of Object.entries(filters)) {
                if (v) params[k] = v;
            }
            const { data } = await api.get("/attendance/snapshot/list", { params });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load snapshots"));
        }
    }, [filters, page]);

    useEffect(() => { Promise.resolve().then(() => { reloadRefs(); reloadPolicies(); }); }, [reloadRefs, reloadPolicies]);
    useEffect(() => { Promise.resolve().then(() => { reloadRows(); }); }, [reloadRows]);

    // 102.4 UAT · Defect Fix 001 — Handlers for the three contextual
    // actions surfaced from the snapshot detail dialog. Each reuses an
    // EXISTING route/endpoint. Zero AAS contract change.
    const openAdministrativeAction = () => {
        if (!detail?.snapshot) return;
        setAaPrefill({
            employeeId: detail.snapshot.employee_id,
            date: detail.snapshot.attendance_date,
        });
        setAaOpen(true);
    };

    const gotoRegularization = () => {
        if (!detail?.snapshot) return;
        // Read-only jump into the existing HR regularization requests
        // list, filtered to this (employee, date). Preserves the
        // "no silent behaviour change" rule — the admin-initiated
        // regularization write path is NOT introduced here (Administrative
        // Action is the operator's write path today).
        const emp = encodeURIComponent(detail.snapshot.employee_id);
        const d = encodeURIComponent(detail.snapshot.attendance_date);
        navigate(`/regularization/requests?scope=all&employee_id=${emp}&from_date=${d}&to_date=${d}&status_filter=all`);
        setDetail(null);
    };

    const gotoEvents = () => {
        if (!detail?.snapshot) return;
        const emp = encodeURIComponent(detail.snapshot.employee_id);
        const d = encodeURIComponent(detail.snapshot.attendance_date);
        navigate(`/admin/attendance/events?employee_id=${emp}&from_date=${d}&to_date=${d}`);
        setDetail(null);
    };

    // Precomputed subject list for the AA dialog — already loaded above.
    // The /hr/employee/list endpoint caps at 500 rows; when the snapshot
    // we're viewing belongs to an employee outside that page, prepend
    // that employee explicitly so the dialog's dropdown can honour the
    // defaultSubjectId prefill. Existing behaviour for in-page employees
    // is preserved.
    const subjectsForAa = useMemo(() => {
        const base = employees.map((e) => ({
            id: e.id,
            label: e.full_name,
            secondary: e.employee_code,
        }));
        const snapEmp = detail?.snapshot?.employee_id;
        if (snapEmp && !base.some((s) => s.id === snapEmp)) {
            base.unshift({
                id: snapEmp,
                label: detail?.snapshot?.employee?.full_name || snapEmp,
                secondary: detail?.snapshot?.employee?.employee_code || "",
            });
        }
        return base;
    }, [employees, detail]);

    const openDetail = async (row) => {
        setDetailLoading(true);
        setDetail({ snapshot: row, events: [], loading: true });
        try {
            const { data } = await api.get(`/attendance/snapshot/${row.employee_id}/${row.attendance_date}`);
            setDetail({ snapshot: data.snapshot, events: data.events, loading: false });
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load detail"));
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const rebuild = async () => {
        if (!detail?.snapshot) return;
        setRebuilding(true);
        try {
            const { data } = await api.post("/attendance/snapshot/rebuild", {
                employee_id: detail.snapshot.employee_id,
                attendance_date: detail.snapshot.attendance_date,
            });
            toast.success("Snapshot rebuilt.");
            setDetail({ ...detail, snapshot: data });
            reloadRows();
        } catch (err) {
            toast.error(formatApiError(err, "Rebuild failed"));
        } finally {
            setRebuilding(false);
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="attendance-snapshots-page">
            <PageHeader
                eyebrow="Attendance · E2.1"
                title="Daily Attendance Snapshots"
                description="Per-(employee, date) materialised view. Each row is the deterministic output of the aggregator brain on that day's events + the shift in effect AS-OF that date."
            />

            {/* Filters */}
            <div className="mt-5 bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="snapshots-filters">
                <div>
                    <Label className="text-xs">Employee</Label>
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.employee_id} onChange={(e) => { setFilters({ ...filters, employee_id: e.target.value }); setPage(1); }} data-testid="filter-employee">
                        <option value="">All</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} · {e.employee_code}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Department</Label>
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.department} onChange={(e) => { setFilters({ ...filters, department: e.target.value }); setPage(1); }} data-testid="filter-department">
                        <option value="">All</option>
                        {(hrMeta.departments || []).map((d) => <option key={d.id || d.name} value={d.name}>{d.name}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Status</Label>
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={filters.attendance_status} onChange={(e) => { setFilters({ ...filters, attendance_status: e.target.value }); setPage(1); }} data-testid="filter-status">
                        <option value="">All</option>
                        {(meta.attendance_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">From date</Label>
                    <Input type="date" value={filters.from_date} onChange={(e) => { setFilters({ ...filters, from_date: e.target.value }); setPage(1); }} data-testid="filter-from" />
                </div>
                <div>
                    <Label className="text-xs">To date</Label>
                    <Input type="date" value={filters.to_date} onChange={(e) => { setFilters({ ...filters, to_date: e.target.value }); setPage(1); }} data-testid="filter-to" />
                </div>
            </div>

            {/* Table */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5 overflow-x-auto" data-testid="snapshots-section">
                <div className="flex justify-between items-center mb-3">
                    <div className="text-xs text-slate-500"><FunnelSimple size={12} className="inline mr-1" /> {total} snapshot(s)</div>
                    {/* Iter 102.2 · single IST disclaimer for the dense table below. */}
                    <div className="text-[10px] uppercase tracking-wider text-slate-400" data-testid="ist-disclaimer">All times are displayed in IST.</div>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Date</th>
                            <th className="text-left px-3 py-2 font-medium">Employee</th>
                            <th className="text-left px-3 py-2 font-medium">Status</th>
                            <th className="text-left px-3 py-2 font-medium">Check-In</th>
                            <th className="text-left px-3 py-2 font-medium">Check-Out</th>
                            <th className="text-left px-3 py-2 font-medium">Gross</th>
                            <th className="text-left px-3 py-2 font-medium">Net</th>
                            <th className="text-left px-3 py-2 font-medium">Break · Idle</th>
                            <th className="text-left px-3 py-2 font-medium">Late · Early</th>
                            <th className="text-left px-3 py-2 font-medium">Anomalies</th>
                            <th className="text-left px-3 py-2 font-medium">Source</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-slate-400">No snapshots for the current filters.</td></tr>}
                        {rows.map((s) => (
                            <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(s)} data-testid={`snapshot-row-${s.id}`}>
                                <td className="px-3 py-2 text-xs font-mono text-slate-600">{s.attendance_date}</td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="text-slate-700">{s.employee?.full_name || s.employee_id}</div>
                                    <div className="text-[10px] text-slate-400 font-mono">{s.employee?.employee_code} · {s.employee?.department}</div>
                                </td>
                                <td className="px-3 py-2 text-xs">
                                    <Badge className={STATUS_BADGE_CLS[s.attendance_status] || "bg-slate-100 text-slate-600"}>{s.attendance_status}</Badge>
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600">{formatTime(s.check_in_time)}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">{formatTime(s.check_out_time)}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">{formatMinutes(s.gross_working_minutes)}</td>
                                <td className="px-3 py-2 text-xs text-slate-800 font-medium">{formatMinutes(s.net_working_minutes)}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">{formatMinutes(s.total_break_minutes)} · {formatMinutes(s.total_idle_minutes)}</td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="flex gap-1">
                                        {s.is_late && <Badge className="bg-orange-100 text-orange-700 text-[10px]">L+{s.late_minutes}</Badge>}
                                        {s.is_early_departure && <Badge className="bg-orange-100 text-orange-700 text-[10px]">E+{s.early_departure_minutes}</Badge>}
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-xs">
                                    {(s.anomalies || []).length > 0 ? (
                                        <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Warning size={10} className="mr-1" />{s.anomalies.length}</Badge>
                                    ) : s.is_regularized ? (
                                        <Badge className="bg-violet-100 text-violet-700 text-[10px]"><FileText size={10} className="mr-1" />Regularized</Badge>
                                    ) : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="px-3 py-2 text-xs">{s.attendance_source && <Badge variant="outline" className="text-[10px]">{s.attendance_source}</Badge>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {total > 50 && (
                    <div className="mt-3 flex justify-end gap-2 text-xs items-center">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                        <span>Page {page} of {Math.ceil(total / 50)}</span>
                        <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage((p) => p + 1)}>Next</Button>
                    </div>
                )}
            </section>

            {/* Detail dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
                <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="snapshot-detail-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {detail?.snapshot?.employee?.full_name || detail?.snapshot?.employee_id} · {detail?.snapshot?.attendance_date}
                        </DialogTitle>
                        <DialogDescription>
                            Aggregated daily attendance + raw event timeline. Snapshot is recomputed on every event (real-time) and verified by the nightly reconciler (02:00 UTC).
                        </DialogDescription>
                    </DialogHeader>
                    {detail?.loading && <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>}
                    {detail && !detail.loading && (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="snapshot-kpis">
                                <Kpi label="Status" value={detail.snapshot.attendance_status} variant={STATUS_BADGE_CLS[detail.snapshot.attendance_status]} />
                                <Kpi label="Source" value={detail.snapshot.attendance_source || "—"} />
                                <Kpi label="Login → Logout" value={`${formatTime(detail.snapshot.login_time)} → ${formatTime(detail.snapshot.logout_time)}`} />
                                <Kpi label="Check-In → Check-Out" value={`${formatTime(detail.snapshot.check_in_time)} → ${formatTime(detail.snapshot.check_out_time)}`} />
                                <Kpi label="Gross Working" value={formatMinutes(detail.snapshot.gross_working_minutes)} />
                                <Kpi label="Net Working" value={formatMinutes(detail.snapshot.net_working_minutes)} />
                                <Kpi label="Break · Idle" value={`${formatMinutes(detail.snapshot.total_break_minutes)} · ${formatMinutes(detail.snapshot.total_idle_minutes)}`} />
                                <Kpi label="Late · Early" value={`${detail.snapshot.late_minutes || 0}m · ${detail.snapshot.early_departure_minutes || 0}m`} />
                            </div>

                            {(detail.snapshot.computation_warnings || []).length > 0 && (
                                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 flex gap-2 items-start">
                                    <Warning size={14} className="shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-semibold">Aggregator warnings:</div>
                                        <ul className="list-disc pl-4 mt-1">
                                            {detail.snapshot.computation_warnings.map((w, i) => <li key={i}>{w}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {(detail.snapshot.breaks_taken || []).length > 0 && (
                                <div className="mt-3" data-testid="snapshot-breaks">
                                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1.5">Breaks taken</div>
                                    <div className="flex flex-wrap gap-2">
                                        {detail.snapshot.breaks_taken.map((b, i) => (
                                            <Badge key={i} variant="outline" className="text-xs"><Coffee size={11} className="mr-1" />{b.name} · {b.duration_minutes}m</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-4">
                                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Events Timeline ({detail.events.length})</div>
                                <ul className="space-y-1.5">
                                    {detail.events.map((e) => (
                                        <li key={e.id} className="flex items-center gap-2 text-xs" data-testid={`detail-event-${e.id}`}>
                                            <span className="font-mono text-slate-500 w-16">{formatTime(e.event_time)}</span>
                                            <Badge variant="outline" className="text-[10px]">{e.event_type.replace("_", " ")}</Badge>
                                            <span className="text-slate-700 flex-1 truncate">{e.break_name || e.remarks || ""}</span>
                                            <Badge variant="outline" className="text-[10px]">{e.source}</Badge>
                                            {e.is_admin_correction && <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Warning size={10} className="mr-1" />Corr</Badge>}
                                        </li>
                                    ))}
                                    {detail.events.length === 0 && <li className="text-slate-400">No raw events on this day.</li>}
                                </ul>
                            </div>

                            <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] text-slate-400 flex justify-between">
                                <span>Computed at {formatDateTimeIST(detail.snapshot.computed_at)}</span>
                                <span>Trigger: {detail.snapshot.compute_trigger}</span>
                            </div>
                        </>
                    )}
                    <DialogFooter className="flex-wrap gap-2">
                        {/* 102.4 UAT · Defect Fix 001 — Contextual action buttons.
                            These are workflow entry points that reuse existing
                            capabilities; no new endpoints, no AAS contract change. */}
                        {can("attendance:admin:action") && detail && (
                            <Button
                                variant="default"
                                onClick={openAdministrativeAction}
                                data-testid="snapshot-administrative-action-btn"
                            >
                                <Gavel size={14} className="mr-1" /> Administrative Action
                            </Button>
                        )}
                        {can("employee:read") && detail && (
                            <Button
                                variant="outline"
                                onClick={gotoRegularization}
                                data-testid="snapshot-goto-regularization-list"
                            >
                                <FileText size={14} className="mr-1" />
                                {detail?.snapshot?.is_regularized ? "View Regularization" : "Start Regularization"}
                            </Button>
                        )}
                        {can("employee:read") && detail && (
                            <Button
                                variant="outline"
                                onClick={gotoEvents}
                                data-testid="snapshot-goto-events"
                            >
                                <ListMagnifyingGlass size={14} className="mr-1" /> View Attendance Events
                            </Button>
                        )}
                        {detail?.snapshot?.anomalies?.length > 0 && (
                            <Button variant="outline" onClick={() => navigate(`/attendance/anomalies?status_filter=all&from_date=${detail.snapshot.attendance_date}&to_date=${detail.snapshot.attendance_date}`)} data-testid="snapshot-goto-anomalies">
                                <Warning size={14} className="mr-1" /> {detail.snapshot.anomalies.length} Anomaly(ies)
                            </Button>
                        )}
                        {can("employee:admin") && detail && (
                            <Button variant="outline" onClick={rebuild} disabled={rebuilding} data-testid="snapshot-rebuild-btn">
                                <ArrowsClockwise size={14} className="mr-1" /> {rebuilding ? "Rebuilding…" : "Force Rebuild"}
                            </Button>
                        )}
                        <Button onClick={() => setDetail(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* 102.4 UAT · Defect Fix 001 — Administrative Action dialog
                mounted at the page root so it survives the parent detail
                dialog being closed (operator flow: close snapshot detail
                → confirm AA). Prefilled with the snapshot's (employee_id,
                attendance_date). Reuses the AAS Attendance wrapper. */}
            <AdministrativeActionDialog
                open={aaOpen}
                onClose={() => setAaOpen(false)}
                onSubmitted={() => reloadRows()}
                module="attendance"
                actionTypes={actionTypes}
                policies={policies}
                subjects={subjectsForAa}
                submitEndpoint="/attendance/administrative-action"
                previewEndpoint="/attendance/administrative-action/preview"
                subjectLabel="Employee"
                defaultSubjectId={aaPrefill.employeeId}
                defaultEffectiveDate={aaPrefill.date}
            />
        </div>
    );
}

function Kpi({ label, value, variant }) {
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            {variant ? (
                <Badge className={`mt-1 ${variant}`}>{value}</Badge>
            ) : (
                <div className="mt-0.5 text-sm font-semibold text-slate-800">{value}</div>
            )}
        </div>
    );
}
